import { type AsyncThunk, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

import type {
  AsyncActionDefinition,
  AsyncActionDefinitions,
  AsyncThunkActionDefinition,
  PageReducerState,
} from './types';

function actionPending<TState>(
  actionName: string,
  actionDefinition: AsyncActionDefinition<TState>,
) {
  return (state: unknown, action: unknown) => {
    const s = state as PageReducerState<TState>;
    s.pageState.isProcessing = true;
    s.pageState.pendingAction = actionName;

    if (actionDefinition.onPending) {
      const a = action as { meta?: { arg?: { args?: unknown[] } } };
      const params = a?.meta?.arg?.args?.[0];

      const changedState = actionDefinition.onPending(s.data, params);
      s.data = changedState;
    }
  };
}

function actionSuccessful<TState>(
  actionName: string,
  actionDefinition: AsyncActionDefinition<TState>,
) {
  return function (state: unknown, action: unknown) {
    const s = state as PageReducerState<TState>;
    s.pageState.isProcessing = false;
    if (s.pageState.pendingAction === actionName) {
      s.pageState.pendingAction = null;
    }

    if (actionDefinition.handlers?.onSuccess) {
      let navigateTo: string | null = null;
      const navigateFx = (url: string) => {
        navigateTo = url;
      };

      const a = action as { meta?: { arg?: { args?: unknown[] } } };
      const params = a?.meta?.arg?.args && a.meta.arg.args[0];

      const changedState = actionDefinition.handlers.onSuccess(s.data, {
        action,
        navigate: navigateFx,
        params,
      });
      s.data = changedState;
      if (navigateTo) {
        s.pageState.navigateTo = navigateTo;
      }
    }
  };
}

function actionError<TState>(actionName: string, actionDefinition: AsyncActionDefinition<TState>) {
  return (state: unknown, action: unknown) => {
    const s = state as PageReducerState<TState>;
    s.pageState.isProcessing = false;
    if (s.pageState.pendingAction === actionName) {
      s.pageState.pendingAction = null;
    }

    if (actionDefinition.handlers?.onError) {
      const a = action as { meta?: { arg?: { args?: unknown[] } }; payload?: unknown };
      const params = a?.meta?.arg?.args?.[0];
      const payload = a.payload;
      // Payload from rejectWithValue is typically `{ errors, code }` for AxiosError;
      // for other errors it's whatever rejectWithValue received. The handler gets
      // the raw payload as `errors` — page-level code narrows.
      const errors =
        payload && typeof payload === 'object' && 'errors' in payload
          ? (payload as { errors: string[] }).errors
          : Array.isArray(payload)
            ? (payload as string[])
            : [String(payload ?? 'Unknown error')];

      const changedState = actionDefinition.handlers.onError(s.data, {
        action,
        params,
        errors,
      });
      s.data = changedState;
    }
  };
}

export function createAsyncActions<TState>(
  name: string,
  asyncActionsDefinitions: AsyncActionDefinitions<TState>,
): {
  asyncActions: Record<string, AsyncThunk<unknown, unknown, object>>;
  asyncThunkActionDefinitions: AsyncThunkActionDefinition[];
} {
  const asyncActions = {} as Record<string, AsyncThunk<unknown, unknown, object>>;
  const asyncThunkActionDefinitions: AsyncThunkActionDefinition[] = [];

  Object.keys(asyncActionsDefinitions || {}).map((key) => {
    const actionDefinition = asyncActionsDefinitions[key];

    const actionCreator = createAsyncThunk(`${name}/${key}`, async (params: unknown, thunkAPI) => {
      try {
        const p = params as { args?: unknown[]; extra?: object };
        const extraThunkApi = {
          ...thunkAPI,
          ...(p.extra ?? {}),
        };
        const args: unknown[] = p.args ?? [params];
        return await actionDefinition.action(...(args as [unknown]), extraThunkApi);
      } catch (error) {
        // Duck-typed check, not instanceof: CJS consumers (axios-mock-adapter
        // in tests) construct errors from axios's CJS build, a different class
        // than the ESM build this file imports.
        if (axios.isAxiosError(error)) {
          return thunkAPI.rejectWithValue({
            errors: error.response?.data?.errors,
            code: error.response?.status,
          });
        }
        return thunkAPI.rejectWithValue(error);
      }
    });
    asyncActions[key] = actionCreator as AsyncThunk<unknown, unknown, object>;

    asyncThunkActionDefinitions.push({
      asyncThunk: actionCreator as AsyncThunk<unknown, unknown, object>,
      pending: actionPending(key, actionDefinition),
      onSuccess: actionSuccessful(key, actionDefinition),
      onError: actionError(key, actionDefinition),
    });
  });

  return { asyncActions, asyncThunkActionDefinitions };
}
